/**
 * figures — set every measurement inside knowledge-base prose in the
 * instrument face.
 *
 * WHY this exists at all. The counter-slip direction says the payload of a
 * GardEn answer is the number: the quantity, the price in сом, the area in m².
 * On the chat and marketplace surfaces those arrive as discrete fields and can
 * simply be given `.g-data`. In the Library they arrive buried inside running
 * prose — "закладывать резерв примерно 10–20% сверх сметы", "около 6–10 м² на
 * литр" — where they are exactly the sentences a first-time renovator is
 * scanning for and exactly the ones that disappear into a wall of body text.
 * This module finds them and hands them back wrapped in `.g-data`, so a reader
 * skimming an article can find the figure before reading the sentence.
 *
 * WHY a tokenizer and not markup. The KB body is a server-owned discriminated
 * union of `{h,p}` and `{callout,text}` — plain strings, no inline markup, and
 * no `dangerouslySetInnerHTML` is permitted (REQ-PRD-§11.2-C). So the split
 * happens here, in React nodes: the rendered text content is byte-identical to
 * the source string, only the spans differ.
 *
 * WHAT it deliberately does NOT match. Anything a letter runs into ("3D",
 * "6 метров") is left as prose via the trailing `(?![\p{L}\d])` guard — a unit
 * has to actually terminate for it to be a unit, otherwise `м` would eat the
 * first letter of every word beginning with it. Headings are never passed
 * through this; a heading is a label, not a measurement.
 *
 * Units cover RU/KY (сом, м², шт) and EN (m², kg, l) because the same article
 * ships in three languages and the KY body reuses the Russian unit forms.
 */
import type { ReactNode } from "react";

/**
 * A run of digits (allowing thousands separators, decimal commas and en/em-dash
 * ranges) with an optional trailing unit. Built fresh per call: a `/g` regex
 * carries `lastIndex` state and this module is re-entered per paragraph.
 */
const FIGURE_SOURCE =
  // number run: either ≥2 chars ending in a digit, or a single digit
  "(?:\\d[\\d\\u00A0\\u202F.,\\u2013\\u2014 -]*\\d|\\d)" +
  // optional unit, longest alternatives first so `мм` wins over `м`
  "(?:[\\s\\u00A0]?(?:м²|м³|м2|м3|мм|см|кг|шт|сом|м|л|г|m²|m³|m2|m3|mm|cm|km|kg|m|l|g|%))?" +
  // the run must terminate — never swallow the head of a word
  "(?![\\p{L}\\d])";

/**
 * Split `text` into prose strings and `.g-data` figure spans.
 *
 * `keyPrefix` must be unique within the parent element (block index is enough)
 * — the returned array is rendered as React children.
 */
export function withFigures(text: string, keyPrefix: string): ReactNode {
  const re = new RegExp(FIGURE_SOURCE, "gu");
  const parts: ReactNode[] = [];
  let cursor = 0;
  let match = re.exec(text);

  while (match !== null) {
    const value = match[0];
    if (value.length === 0) {
      // Zero-length match cannot happen with this source, but a runaway loop
      // here would hang the render — advance defensively.
      re.lastIndex += 1;
      match = re.exec(text);
      continue;
    }
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    // The figure keeps the prose's size. It used to step down to 0.94em,
    // because JetBrains Mono ran optically larger than Manrope; IBM Plex Mono
    // runs slightly smaller than Onest (x-height 0.516 vs 0.527em, digits
    // 0.600 vs 0.672em; measured 2026-10-09), so a step down would shrink it
    // below the sentence it sits in. The negative word-spacing pulls a
    // monospace space (a full 0.6em advance, against Onest's 0.27em) back
    // toward the prose spaces around it, so "90 000 сом" does not read as a
    // double gap mid-sentence; `nowrap` keeps a measurement whole — "1 cm"
    // must never break across two lines.
    parts.push(
      <span
        key={`${keyPrefix}-${String(match.index)}`}
        className="g-data whitespace-nowrap text-[var(--g-figure)] [word-spacing:-0.16em]"
      >
        {value}
      </span>,
    );
    cursor = match.index + value.length;
    match = re.exec(text);
  }

  if (cursor === 0) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
